import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { LyricTimingV1, timingCounts } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { LyricTimingPill } from './LyricTimingPill';
import { agoParts } from './lyricTimingFormat';
import { lyricTimingMessages } from './lyricTimingMessages';
import { PanelHead } from '../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric timing; each part uses its own slice of it */
import styles from './LyricTiming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricTimingHeadProps {
  timing: LyricTimingV1;
  isSaving: boolean;
  isResyncing: boolean;
  /** Undefined: no Re-sync (the demo artist). */
  onResync?: () => void;
}

/**
 * The head of the sync editor (design s8): "Lyric timing", the counts line ("SYNCED 2 MIN AGO ·
 * 2 CHANGED BY YOU · 3 TO CHECK"), the state pill and Re-sync. While a sync runs, Re-sync is off.
 */
const LyricTimingHeadComponent: React.FC<LyricTimingHeadProps> = ({ timing, isSaving, isResyncing, onResync }) => {
  const intl = useIntl();
  const counts = useMemo(() => timingCounts(timing), [timing]);

  const parts: string[] = [];
  if (timing.status === 'failed') {
    parts.push(intl.formatMessage(lyricTimingMessages.lineCount, { count: counts.lineCount }));
    const missing = counts.lineCount - counts.timedCount;
    if (missing > 0) {
      parts.push(intl.formatMessage(lyricTimingMessages.noTimeCount, { count: missing }));
    }
  } else if (timing.status === 'synced') {
    const ago = agoParts(timing.syncedAt, Date.now());
    if (ago !== null) {
      const when = intl.formatRelativeTime(ago.value, ago.unit, { numeric: 'auto', style: 'short' });
      parts.push(intl.formatMessage(lyricTimingMessages.syncedAgo, { ago: when }));
    } else {
      parts.push(intl.formatMessage(lyricTimingMessages.lineCount, { count: counts.lineCount }));
    }
  } else {
    parts.push(intl.formatMessage(lyricTimingMessages.lineCount, { count: counts.lineCount }));
  }
  if (counts.editedCount > 0) {
    parts.push(intl.formatMessage(lyricTimingMessages.changedByYou, { count: counts.editedCount }));
  }
  if (timing.status === 'synced' && counts.checkCount > 0) {
    parts.push(intl.formatMessage(lyricTimingMessages.toCheck, { count: counts.checkCount }));
  }
  if (isSaving) {
    parts.push(intl.formatMessage(lyricTimingMessages.saving));
  }

  let pill = <LyricTimingPill tone="ok" label={intl.formatMessage(lyricTimingMessages.pillSynced)} />;
  if (timing.status === 'syncing') {
    pill = <LyricTimingPill tone="run" label={intl.formatMessage(lyricTimingMessages.pillSyncing)} />;
  } else if (timing.status === 'failed') {
    pill = <LyricTimingPill tone="bad" label={intl.formatMessage(lyricTimingMessages.pillFailed)} />;
  }

  return (
    <PanelHead
      title={intl.formatMessage(lyricTimingMessages.title)}
      subtitle={
        <span className={styles.counts} data-testid="lyric-timing-counts">
          {parts.join(' · ')}
        </span>
      }
      right={
        <>
          {pill}
          {onResync !== undefined && (
            <button
              type="button"
              className={styles.button}
              onClick={onResync}
              disabled={isResyncing || timing.status === 'syncing'}
            >
              {intl.formatMessage(lyricTimingMessages.resync)}
            </button>
          )}
        </>
      }
    />
  );
};

export const LyricTimingHead = TypedMemo(LyricTimingHeadComponent);
